import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import ChainRunOverlay from "./ChainRunOverlay";
import { FoodDetailDrawer } from "./FoodDetailDrawer";
import RunHistoryPanel from "./RunHistoryPanel";
import SourcingFinderDialog from "./SourcingFinderDialog";
import { useRegisterBackgroundRun } from "./backgroundRuns";
import { labelForRun } from "./runStore";
import { Badge, PrimaryButton, SecondaryButton, Toast } from "./shared";
import {
  ItemImages,
  PassioFood,
  ReplacementCandidate,
  ReplacementItem,
  ReplacementResult,
  ReplacementReviewEntry,
  ReplacementUnmatchedEntry,
} from "./types";
import {
  CatalogApi,
  ChainProgress,
  extractApiError,
  extractReplacementResult,
} from "./useCatalogApi";
import { useRunHistory } from "./useRunHistory";

/** What we persist per replacement run so it can be fully re-opened. */
interface ReplacementRunPayload {
  input: string;
  result: ReplacementResult;
}

// Friendly labels for the replacement chain's steps (see the chain builder doc).
const REPLACEMENT_STEP_LABELS: Record<string, string> = {
  "node-1": "Reading your grocery list",
  "node-2": "Searching the food database",
  "node-3": "Filtering to relevant options",
  "node-4": "Narrowing to the best candidates",
  "node-5": "Ranking replacements (Dr. Berg rules)",
  "node-6": "Preparing your results",
};

const MAX_INPUT_BYTES = 64 * 1024; // grocery lists are small; keep payloads sane
const byteLen = (s: string): number =>
  typeof TextEncoder !== "undefined"
    ? new TextEncoder().encode(s).length
    : unescape(encodeURIComponent(s)).length;

// 0..1 score -> tone. Mirrors the keto/upf semantics (1 = great).
const scoreTone = (v?: number): "green" | "amber" | "red" | "gray" => {
  if (typeof v !== "number") return "gray";
  if (v >= 0.7) return "green";
  if (v >= 0.4) return "amber";
  return "red";
};

const pct = (v?: number): string =>
  typeof v === "number" ? `${Math.round(v * 100)}` : "—";

const ScorePill: React.FC<{ label: string; value?: number; hint?: string }> = ({
  label,
  value,
  hint,
}) => {
  const tone = scoreTone(value);
  const cls = {
    green: "bg-green-100 text-green-800",
    amber: "bg-amber-100 text-amber-800",
    red: "bg-red-100 text-red-700",
    gray: "bg-gray-100 text-gray-500",
  }[tone];
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-semibold ${cls}`}
      title={hint || `${label} score (0–100, higher is better)`}
    >
      {label} {pct(value)}
    </span>
  );
};

// Pull a nutrient value by trying several key spellings (case-insensitive).
const nutOf = (
  n: Record<string, number> | undefined,
  keys: string[]
): number | undefined => {
  if (!n) return undefined;
  for (const k of keys) {
    const v = n[k];
    if (typeof v === "number" && !Number.isNaN(v)) return v;
  }
  const lower: Record<string, number> = {};
  for (const [kk, vv] of Object.entries(n)) {
    if (typeof vv === "number") lower[kk.toLowerCase()] = vv;
  }
  for (const k of keys) {
    const v = lower[k.toLowerCase()];
    if (typeof v === "number" && !Number.isNaN(v)) return v;
  }
  return undefined;
};

const fmtNum = (v: number): string =>
  Math.abs(v) >= 10 || Number.isInteger(v)
    ? `${Math.round(v)}`
    : `${Math.round(v * 10) / 10}`;

const Stat: React.FC<{ label: string; value?: number; unit?: string }> = ({
  label,
  value,
  unit = "",
}) =>
  typeof value === "number" ? (
    <div className="flex min-w-[54px] flex-col items-center rounded-md bg-gray-50 px-2.5 py-1.5">
      <span className="text-sm font-bold text-gray-900">
        {fmtNum(value)}
        {unit}
      </span>
      <span className="text-[10px] font-medium uppercase tracking-wide text-gray-400">
        {label}
      </span>
    </div>
  ) : null;

// A clean scored-product card used for both the original and each candidate.
const ProductCard: React.FC<{
  name?: string;
  brand?: string;
  serving?: string;
  nutrients?: Record<string, number>;
  netCarbs?: number;
  ketoScore?: number;
  upfScore?: number;
  country?: string;
  notes?: string[];
  ingredients?: string;
  aiScore?: number;
  aiReason?: string;
  refCode?: string;
  /**
   * Convention-based CDN images (product + brand); each blank when none exists.
   * The card shows the product image when present, otherwise the brand image.
   */
  images?: ItemImages;
  onViewFull?: () => void;
  tone?: "neutral" | "original";
}> = ({
  name,
  brand,
  serving,
  nutrients,
  netCarbs,
  ketoScore,
  upfScore,
  country,
  notes,
  ingredients,
  aiScore,
  aiReason,
  refCode,
  images,
  onViewFull,
  tone = "neutral",
}) => {
  const thumb = images?.product || images?.brand || "";
  const calories = nutOf(nutrients, [
    "calories_kcal",
    "energy_kcal",
    "calories",
    "kcal",
    "energy",
  ]);
  const protein = nutOf(nutrients, ["protein_g", "protein"]);
  const fat = nutOf(nutrients, ["fat_g", "total_fat_g", "totalfat_g", "fat"]);
  const carbs = nutOf(nutrients, [
    "carbs_g",
    "carbohydrates_g",
    "total_carbs_g",
    "carbohydrate_g",
    "carbs",
  ]);
  const netCarbsVal =
    typeof netCarbs === "number" ? netCarbs : nutOf(nutrients, ["net_carbs_g"]);
  const hasMacros = [calories, protein, fat, carbs, netCarbsVal].some(
    (v) => typeof v === "number"
  );

  return (
    <div
      className={`rounded-lg border p-3.5 ${
        tone === "original"
          ? "border-gray-300 bg-gray-50"
          : "border-gray-200 bg-white"
      }`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex min-w-0 items-start gap-3">
          {thumb && (
            <img
              src={thumb}
              alt=""
              className="h-12 w-12 flex-shrink-0 rounded-md border border-gray-200 bg-white object-contain"
            />
          )}
          <div className="min-w-0">
            <div className="text-[15px] font-semibold leading-tight text-gray-900">
              {name || "Unknown product"}
            </div>
            <div className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-xs text-gray-500">
              {brand && <span className="font-medium">{brand}</span>}
              {serving && (
                <span className="inline-flex items-center rounded bg-gray-100 px-1.5 py-0.5 font-medium text-gray-600">
                  {serving}
                </span>
              )}
              {country && <span>{country}</span>}
            </div>
          </div>
        </div>
        {refCode && onViewFull && (
          <button
            type="button"
            onClick={onViewFull}
            className="inline-flex flex-shrink-0 items-center gap-1 rounded-md border border-indigo-200 bg-indigo-50 px-2.5 py-1.5 text-xs font-semibold text-indigo-700 hover:bg-indigo-100"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 20 20"
              fill="currentColor"
              className="h-3.5 w-3.5"
            >
              <path d="M10 12.5a2.5 2.5 0 100-5 2.5 2.5 0 000 5z" />
              <path
                fillRule="evenodd"
                d="M.664 10.59a1.65 1.65 0 010-1.186A10.004 10.004 0 0110 3.5c4.257 0 7.893 2.66 9.336 6.41.147.381.146.804 0 1.186A10.004 10.004 0 0110 16.5c-4.257 0-7.893-2.66-9.336-5.91zM14 10a4 4 0 11-8 0 4 4 0 018 0z"
                clipRule="evenodd"
              />
            </svg>
            View full record
          </button>
        )}
      </div>

      <div className="mt-2.5 flex flex-wrap items-center gap-1.5">
        <ScorePill label="Keto" value={ketoScore} />
        <ScorePill
          label="UPF"
          value={upfScore}
          hint="Whole-food score (100 = least processed)"
        />
        {typeof aiScore === "number" && (
          <span
            className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-bold text-white ${
              {
                green: "bg-green-600",
                amber: "bg-amber-500",
                red: "bg-red-500",
                gray: "bg-gray-400",
              }[scoreTone(aiScore)]
            }`}
            title="AI rank score (0–100)"
          >
            AI {pct(aiScore)}
          </span>
        )}
      </div>

      {hasMacros && (
        <div className="mt-2.5 flex flex-wrap gap-1.5">
          <Stat label="Cal" value={calories} />
          <Stat label="Protein" value={protein} unit="g" />
          <Stat label="Fat" value={fat} unit="g" />
          <Stat label="Carbs" value={carbs} unit="g" />
          <Stat label="Net carbs" value={netCarbsVal} unit="g" />
        </div>
      )}

      {aiReason && (
        <p className="mt-2.5 border-l-2 border-indigo-200 pl-2.5 text-xs italic text-gray-600">
          {aiReason}
        </p>
      )}

      {notes && notes.length > 0 && (
        <ul className="mt-2 space-y-0.5">
          {notes.slice(0, 4).map((n, i) => (
            <li key={i} className="text-xs text-amber-700">
              ⚠ {n}
            </li>
          ))}
        </ul>
      )}

      {ingredients && (
        <p className="mt-2.5 line-clamp-2 text-xs leading-snug text-gray-500">
          <span className="font-semibold text-gray-600">Ingredients: </span>
          {ingredients}
        </p>
      )}
    </div>
  );
};

type Rating = "good" | "bad";
interface RatingState {
  rating?: Rating;
  note: string;
}

interface GroceryReplacementTabProps {
  api: CatalogApi;
  /** Web product finder chain — powers "Find more options" for sourcing gaps. */
  altFinderChainId: string;
  /** Whether an ingest chain is configured (needed to add found products). */
  ingestConfigured: boolean;
  /** Kick off a background ingest run (same as the Add products tab). */
  onIngest: (foodInput: string, count: number) => Promise<void> | void;
}

const ratingKey = (itemIdx: number, cand: ReplacementCandidate): string =>
  `${itemIdx}:${cand.ref_code || cand.id || cand.name || "c"}`;

const GroceryReplacementTab: React.FC<GroceryReplacementTabProps> = ({
  api,
  altFinderChainId,
  ingestConfigured,
  onIngest,
}) => {
  const [groceryInput, setGroceryInput] = useState("");
  const [running, setRunning] = useState(false);
  // The replacement run keeps going in the background when the progress dialog
  // is hidden — this only controls whether the (blocking) overlay is shown.
  const [runHidden, setRunHidden] = useState(false);
  const [progress, setProgress] = useState<ChainProgress | null>(null);
  const [credits, setCredits] = useState<number | null>(null);
  const [error, setError] = useState("");
  const [runError, setRunError] = useState("");
  const abortRef = useRef<AbortController | null>(null);
  const resultsRef = useRef<HTMLDivElement | null>(null);

  const [result, setResult] = useState<ReplacementResult | null>(null);
  const [chainRunId, setChainRunId] = useState("");
  const [ranInput, setRanInput] = useState("");

  // Persisted run history (localStorage; see runStore.ts) so a closed tab or an
  // accidental view change doesn't lose a completed run.
  const history = useRunHistory<ReplacementRunPayload>("replacement");

  const [ratings, setRatings] = useState<Record<string, RatingState>>({});
  // Optional notes for grocery items that got ZERO suggestions, keyed by item
  // index. These items are always recorded on submit (sourcing signal); the
  // note is just extra context.
  const [unmatchedNotes, setUnmatchedNotes] = useState<Record<number, string>>(
    {}
  );
  const [submitting, setSubmitting] = useState(false);
  const [submitMsg, setSubmitMsg] = useState("");
  const [submitErr, setSubmitErr] = useState("");

  // "Find more options" (web product finder) dialog — non-blocking; its state
  // lives in the dialog and is keyed to the run so it resets per grocery run.
  const [finderOpen, setFinderOpen] = useState(false);
  // Sticky "run finished — here's what to do next" nudge.
  const [runDoneToast, setRunDoneToast] = useState(false);

  // Lazy full-record drawer (reuses the shared food display component).
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerLoading, setDrawerLoading] = useState(false);
  const [drawerFood, setDrawerFood] = useState<PassioFood | null>(null);
  const [drawerTitle, setDrawerTitle] = useState("");
  const [drawerError, setDrawerError] = useState("");

  const inputBytes = byteLen(groceryInput);
  const overLimit = inputBytes > MAX_INPUT_BYTES;
  const canRun = !running && !overLimit && groceryInput.trim().length > 0;

  const run = async () => {
    setError("");
    setRunError("");
    setSubmitMsg("");
    setSubmitErr("");
    if (!groceryInput.trim()) {
      setError("Paste a grocery list first.");
      return;
    }
    if (overLimit) {
      setError("That list is too large — trim it and try again.");
      return;
    }
    const controller = new AbortController();
    abortRef.current = controller;
    setRunning(true);
    setRunHidden(false);
    setProgress(null);
    setCredits(null);
    setResult(null);
    setRatings({});
    setUnmatchedNotes({});
    setRunDoneToast(false);
    try {
      const resp = await api.runConfiguredBergGroceryReplacements(
        { grocery_input: groceryInput.trim() },
        {
          signal: controller.signal,
          onStatus: (_s, c) => {
            if (typeof c === "number") setCredits(c);
          },
          onProgress: setProgress,
        }
      );
      const runId = resp.id || `replace-${Date.now()}`;
      const trimmedInput = groceryInput.trim();
      const extracted = extractReplacementResult(resp);
      setChainRunId(runId);
      setRanInput(trimmedInput);
      setResult(extracted);
      setRunDoneToast(true);
      const items = extracted.items || [];
      const withOptions = items.filter(
        (it) => (it.suggested_replacements || []).length > 0
      ).length;
      history.saveRun({
        id: runId,
        kind: "replacement",
        status: "completed",
        label: labelForRun(trimmedInput, items.length),
        createdAt: Date.now(),
        updatedAt: Date.now(),
        inputSummary: `${items.length} item${items.length === 1 ? "" : "s"}`,
        chainRunId: resp.id || undefined,
        credits:
          (resp as { total_ai_credits_used?: number }).total_ai_credits_used ??
          null,
        payload: { input: trimmedInput, result: extracted },
        counts: { items: items.length, "with options": withOptions },
      });
    } catch (err) {
      if ((err as Error)?.name === "AbortError") {
        setError("Cancelled.");
      } else {
        setRunError(extractApiError(err, "Replacement run failed"));
      }
    } finally {
      setRunning(false);
      abortRef.current = null;
    }
  };

  const cancel = useCallback(() => abortRef.current?.abort(), []);

  // Surface this run in the shared background-runs tray (bottom-right) while it
  // runs, instead of a bespoke floating pill. "View" reopens the progress dialog.
  useRegisterBackgroundRun(running, {
    id: "grocery-replacement",
    title: "Finding replacements",
    detail: progress?.currentNodeId
      ? REPLACEMENT_STEP_LABELS[progress.currentNodeId] || "Working…"
      : "Starting…",
    onView: () => setRunHidden(false),
    onCancel: cancel,
  });

  // Reload a previous run's input + results (survives tab close / view change).
  const reloadRun = (run: {
    id: string;
    chainRunId?: string;
    payload?: ReplacementRunPayload;
  }) => {
    if (!run.payload) return;
    setGroceryInput(run.payload.input);
    setRanInput(run.payload.input);
    setResult(run.payload.result);
    setChainRunId(run.chainRunId || run.id);
    setRatings({});
    setUnmatchedNotes({});
    setError("");
    setRunError("");
    setSubmitErr("");
    setSubmitMsg("");
    setRunDoneToast(false);
  };

  const setRating = (key: string, rating: Rating) =>
    setRatings((r) => {
      const cur = r[key] || { note: "" };
      // Toggle off if the same button is pressed again.
      const next: RatingState =
        cur.rating === rating
          ? { ...cur, rating: undefined }
          : { ...cur, rating };
      return { ...r, [key]: next };
    });

  const setNote = (key: string, note: string) =>
    setRatings((r) => ({ ...r, [key]: { ...(r[key] || {}), note } }));

  const buildReviews = (): ReplacementReviewEntry[] => {
    if (!result) return [];
    const out: ReplacementReviewEntry[] = [];
    result.items.forEach((item, i) => {
      (item.suggested_replacements || []).forEach((cand) => {
        const st = ratings[ratingKey(i, cand)];
        if (!st?.rating) return;
        out.push({
          original: {
            ref_code: item.original_item_details?.ref_code,
            name: item.original_item_details?.name || item.original_item,
            brand: item.original_item_details?.brand,
            ingredients: item.original_item_details?.ingredients,
          },
          replacement: {
            ref_code: cand.ref_code,
            name: cand.name,
            brand: cand.brand,
            ingredients: cand.ingredients,
          },
          rating: st.rating,
          note: st.note?.trim() || undefined,
        });
      });
    });
    return out;
  };

  const ratedCount = useMemo(
    () => Object.values(ratings).filter((r) => r.rating).length,
    [ratings]
  );

  // Every grocery item the chain returned no suggestions for. Recorded on submit
  // so we can later see what we need to source / feed into the ingest chain.
  const buildUnmatched = (): ReplacementUnmatchedEntry[] => {
    if (!result) return [];
    const out: ReplacementUnmatchedEntry[] = [];
    result.items.forEach((item, i) => {
      if ((item.suggested_replacements || []).length > 0) return;
      const od = item.original_item_details;
      out.push({
        original_item: item.original_item,
        name: od?.name,
        brand: od?.brand,
        ref_code: od?.ref_code,
        ingredients: od?.ingredients,
        note: unmatchedNotes[i]?.trim() || undefined,
      });
    });
    return out;
  };

  const unmatchedCount = useMemo(
    () =>
      (result?.items || []).filter(
        (it) => (it.suggested_replacements || []).length === 0
      ).length,
    [result]
  );

  // Item names split by whether they found suggestions — fed to the web finder.
  const gapItemNames = useMemo(
    () =>
      (result?.items || [])
        .filter((it) => (it.suggested_replacements || []).length === 0)
        .map((it) => it.original_item)
        .filter(Boolean),
    [result]
  );
  const matchedItemNames = useMemo(
    () =>
      (result?.items || [])
        .filter((it) => (it.suggested_replacements || []).length > 0)
        .map((it) => it.original_item)
        .filter(Boolean),
    [result]
  );

  const canSubmit = ratedCount > 0 || unmatchedCount > 0;

  const submitReview = async () => {
    const reviews = buildReviews();
    const unmatched = buildUnmatched();
    setSubmitErr("");
    setSubmitMsg("");
    if (reviews.length === 0 && unmatched.length === 0) {
      setSubmitErr(
        "Rate at least one suggestion (👍/👎), or run a list first — nothing to record yet."
      );
      return;
    }
    setSubmitting(true);
    try {
      const res = await api.saveReplacementReview({
        chainRunId: chainRunId || "unknown",
        reviews,
        unmatched,
        groceryInput: ranInput,
      });
      const rated = res.review_count ?? reviews.length;
      const missing = res.unmatched_count ?? unmatched.length;
      const parts = [
        `${rated} rating${rated === 1 ? "" : "s"}`,
        `${missing} item${missing === 1 ? "" : "s"} with no suggestions`,
      ];
      setSubmitMsg(
        `Saved ${parts.join(
          " and "
        )}. Thanks — this feedback (and the sourcing gaps) is stored for tuning the tool.`
      );
      // Record on the persisted run so its history entry reflects the review.
      if (chainRunId) {
        const prev = history.getRun(chainRunId);
        history.patchRun(chainRunId, {
          counts: { ...(prev?.counts || {}), rated, reviewed: 1 },
        });
      }
    } catch (err) {
      setSubmitErr(extractApiError(err, "Failed to save review"));
    } finally {
      setSubmitting(false);
    }
  };

  const setUnmatchedNote = (itemIdx: number, note: string) =>
    setUnmatchedNotes((n) => ({ ...n, [itemIdx]: note }));

  // Once results land (and the overlay is gone), glide down to them so it's
  // obvious the run finished and there's something to review.
  useEffect(() => {
    if (result && !running) {
      const id = window.setTimeout(
        () =>
          resultsRef.current?.scrollIntoView({
            behavior: "smooth",
            block: "start",
          }),
        80
      );
      return () => window.clearTimeout(id);
    }
    return undefined;
  }, [result, running]);

  const viewFull = async (refCode?: string, title?: string) => {
    if (!refCode) return;
    setDrawerOpen(true);
    setDrawerLoading(true);
    setDrawerError("");
    setDrawerFood(null);
    setDrawerTitle(title || "");
    try {
      const full = await api.getFoodByRefCode(refCode);
      if (!full) setDrawerError("Could not load the full record.");
      else setDrawerFood(full);
    } catch (err) {
      setDrawerError(extractApiError(err, "Failed to load record"));
    } finally {
      setDrawerLoading(false);
    }
  };

  const summary = result?.summary as
    | { total_items?: number; items_with_replacements?: number }
    | undefined;

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-gray-900">
          Grocery Replacement Test
        </h3>
        <p className="mt-1 text-sm text-gray-600">
          Paste a grocery list and we&apos;ll suggest better keto / low-UPF
          replacements (Dr. Berg rules) from the database. Rate each suggestion
          👍 / 👎 with an optional note — your feedback is saved to help tune
          the tool.
        </p>
      </div>

      {/* Input */}
      <div className="rounded-lg border border-gray-200 bg-white p-4">
        <label className="text-sm font-semibold text-gray-800">
          Your grocery list
          <span className="ml-2 text-xs font-normal text-gray-400">
            any format — one per line, comma-separated, or a messy paste
          </span>
        </label>
        <textarea
          value={groceryInput}
          onChange={(e) => setGroceryInput(e.target.value)}
          rows={7}
          disabled={running}
          placeholder={
            "White bread\n" +
            "Chocolate chip energy bar\n" +
            "Coca-Cola\n" +
            "Tortilla chips\n" +
            "Vanilla ice cream"
          }
          className="mt-2 w-full rounded-md border border-gray-300 p-3 font-mono text-sm focus:border-indigo-500 focus:outline-none"
        />
        <div className="mt-3 flex flex-wrap items-center gap-3">
          <PrimaryButton type="button" onClick={run} disabled={!canRun}>
            {running ? "Finding replacements…" : "Find replacements →"}
          </PrimaryButton>
          {overLimit && (
            <span className="text-sm font-medium text-red-600">
              That list is too large — trim it.
            </span>
          )}
        </div>
        {error && <p className="mt-2 text-sm text-amber-700">{error}</p>}
      </div>

      {/* Run history — reload a previous run if the tab was closed or the view
          changed by accident. Stored in this browser only (see runStore.ts). */}
      <RunHistoryPanel
        runs={history.runs}
        onReload={reloadRun}
        onDelete={history.removeRun}
        onClearAll={history.clearAll}
        title="Recent replacement runs"
        subtitle="Reload a previous run's results here — they're saved in this browser so you won't lose them if you close the tab or switch views."
        renderMeta={(run) => {
          const c = run.counts || {};
          const bits: string[] = [];
          if (typeof c.items === "number")
            bits.push(`${c.items} item${c.items === 1 ? "" : "s"}`);
          if (typeof c["with options"] === "number")
            bits.push(`${c["with options"]} with options`);
          if (c.rated) bits.push(`${c.rated} rated`);
          return (
            <>
              {run.status === "completed" ? "Ready" : run.status}
              {bits.length > 0 ? ` · ${bits.join(" · ")}` : ""}
              {c.reviewed ? " · ✓ reviewed" : ""}
            </>
          );
        }}
      />

      {/* Results */}
      {result && (
        <div ref={resultsRef} className="scroll-mt-4 space-y-4">
          {/* Sticky action bar so Submit + progress stay visible while you
              scroll through every item making notes. */}
          <div className="sticky top-0 z-30 flex flex-wrap items-center justify-between gap-3 rounded-lg border border-gray-200 bg-white/95 px-4 py-3 shadow-md backdrop-blur">
            <div className="min-w-0 text-sm">
              <span className="font-semibold text-gray-900">Results</span>
              <span className="text-gray-500">
                {" "}
                · {result.items.length} item
                {result.items.length === 1 ? "" : "s"}
                {summary?.items_with_replacements != null &&
                  ` · ${summary.items_with_replacements} with suggestions`}
              </span>
            </div>
            <div className="flex items-center gap-3">
              <span className="text-xs text-gray-500">
                <span className="font-medium text-gray-700">
                  {ratedCount} rated
                </span>
                {unmatchedCount > 0 && (
                  <>
                    {" · "}
                    <span className="font-medium text-amber-700">
                      {unmatchedCount} gap{unmatchedCount === 1 ? "" : "s"}
                    </span>
                  </>
                )}
              </span>
              <SecondaryButton
                type="button"
                onClick={() => setFinderOpen(true)}
                disabled={
                  !altFinderChainId ||
                  (gapItemNames.length === 0 && matchedItemNames.length === 0)
                }
                title={
                  altFinderChainId
                    ? "Web-search keto/UPF alternatives for sourcing gaps and add them"
                    : "Set the web product finder chain id in ⚙ Settings"
                }
              >
                🔎 Find more options
                {unmatchedCount > 0 ? ` (${unmatchedCount})` : ""}
              </SecondaryButton>
              <PrimaryButton
                type="button"
                onClick={submitReview}
                disabled={submitting || !canSubmit}
              >
                {submitting ? "Saving…" : "Submit review"}
              </PrimaryButton>
            </div>
          </div>

          {submitMsg && (
            <div className="rounded-md border border-green-200 bg-green-50 p-3 text-sm text-green-700">
              {submitMsg}
            </div>
          )}
          {submitErr && (
            <div className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700">
              {submitErr}
            </div>
          )}

          {result.items.map((item, i) => (
            <ReplacementItemBlock
              key={`${i}:${item.original_item}`}
              item={item}
              itemIdx={i}
              ratings={ratings}
              onRate={setRating}
              onNote={setNote}
              onViewFull={viewFull}
              unmatchedNote={unmatchedNotes[i] || ""}
              onUnmatchedNote={setUnmatchedNote}
            />
          ))}
        </div>
      )}

      {/* Loading overlay (nice per-step progress). Hideable so the run can go to
          the background and free the page, just like the ingest chain. */}
      <ChainRunOverlay
        open={running && !runHidden}
        progress={progress}
        credits={credits}
        title="Finding better replacements…"
        labels={REPLACEMENT_STEP_LABELS}
        onCancel={cancel}
        onHide={() => setRunHidden(true)}
      />

      {/* While the progress overlay is hidden the run stays visible in the shared
          background-runs tray (bottom-right) — no separate floating pill. */}

      {/* Failure dialog handled inline (simple, this is a test tool) */}
      {runError && (
        <div className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700">
          {runError}
        </div>
      )}

      <FoodDetailDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        subtitle="Full record"
        title={drawerTitle}
        loading={drawerLoading}
        error={drawerError}
        food={drawerFood}
      />

      {/* Web product finder — kept mounted (keyed to the run) so a search keeps
          going and its results survive hiding/reopening the dialog. */}
      {result && (
        <SourcingFinderDialog
          key={chainRunId || "finder"}
          open={finderOpen}
          onClose={() => setFinderOpen(false)}
          onReopen={() => setFinderOpen(true)}
          api={api}
          chainId={altFinderChainId}
          gapItems={gapItemNames}
          matchedItems={matchedItemNames}
          ingestConfigured={ingestConfigured}
          onIngest={onIngest}
        />
      )}

      {/* Completion nudge: results are in, but there are still steps (rate +
          submit, and source the gaps). Sticky so it isn't missed. */}
      <Toast
        open={runDoneToast && !!result && !running}
        tone="success"
        message={
          <span>
            ✓ Replacements ready. Next: rate the picks 👍/👎 and{" "}
            <span className="font-bold">Submit review</span>.
            {unmatchedCount > 0 && (
              <>
                {" "}
                {unmatchedCount} item{unmatchedCount === 1 ? "" : "s"} had no
                suggestions — use{" "}
                <span className="font-bold">Find more options</span> to source
                them.
              </>
            )}
          </span>
        }
        actionLabel={unmatchedCount > 0 ? "Find more options" : undefined}
        onAction={unmatchedCount > 0 ? () => setFinderOpen(true) : undefined}
        onClose={() => setRunDoneToast(false)}
        duration={0}
      />
    </div>
  );
};

// One grocery item: the original (scored) + its ranked replacement cards.
const ReplacementItemBlock: React.FC<{
  item: ReplacementItem;
  itemIdx: number;
  ratings: Record<string, RatingState>;
  onRate: (key: string, rating: Rating) => void;
  onNote: (key: string, note: string) => void;
  onViewFull: (refCode?: string, title?: string) => void;
  unmatchedNote: string;
  onUnmatchedNote: (itemIdx: number, note: string) => void;
}> = ({
  item,
  itemIdx,
  ratings,
  onRate,
  onNote,
  onViewFull,
  unmatchedNote,
  onUnmatchedNote,
}) => {
  const od = item.original_item_details;
  const suggestions = item.suggested_replacements || [];
  return (
    <div className="rounded-lg border border-gray-200 bg-white p-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h4 className="text-base font-semibold text-gray-900">
          {item.original_item}
        </h4>
        <div className="flex items-center gap-2">
          {item.original_already_good && (
            <Badge tone="green">Original already good</Badge>
          )}
          {!item.had_internal_matches && (
            <Badge tone="gray">No database matches</Badge>
          )}
        </div>
      </div>

      {/* Original scored product (why replace it) */}
      <div className="mt-3">
        <div className="mb-1 text-xs font-semibold uppercase tracking-wide text-gray-400">
          Your item
        </div>
        {od ? (
          <ProductCard
            tone="original"
            name={od.name}
            brand={od.brand}
            serving={od.serving}
            nutrients={od.nutrients}
            netCarbs={od.net_carbs_g}
            ketoScore={od.keto_score}
            upfScore={od.upf_score}
            notes={od.notes}
            ingredients={od.ingredients}
            refCode={od.ref_code}
            images={od.images}
            onViewFull={() => onViewFull(od.ref_code, od.name)}
          />
        ) : (
          <div className="rounded-md border border-dashed border-gray-300 bg-gray-50 p-3 text-xs text-gray-500">
            We couldn&apos;t score this item against the database (no close
            match found).
          </div>
        )}
      </div>

      {/* Suggested replacements */}
      <div className="mt-4">
        <div className="mb-1 text-xs font-semibold uppercase tracking-wide text-gray-400">
          Suggested replacements ({suggestions.length})
        </div>
        {suggestions.length === 0 ? (
          <div className="rounded-md border border-dashed border-amber-300 bg-amber-50 p-3">
            <div className="text-sm text-amber-800">
              <span className="font-semibold">No suggestions found.</span> For
              many categories that&apos;s the expected answer (better to show
              nothing than an ultra-processed pick) — but this item is
              automatically recorded on submit as a{" "}
              <span className="font-semibold">sourcing gap</span> so we know to
              add options for it to the catalog.
            </div>
            <input
              type="text"
              value={unmatchedNote}
              onChange={(e) => onUnmatchedNote(itemIdx, e.target.value)}
              placeholder="Optional note — e.g. what we should source / ingest for this"
              className="mt-2 w-full rounded-md border border-amber-300 bg-white px-2 py-1 text-sm focus:border-amber-500 focus:outline-none"
            />
          </div>
        ) : (
          <div className="space-y-3">
            {suggestions.map((cand) => {
              const key = ratingKey(itemIdx, cand);
              const st = ratings[key];
              const ring =
                st?.rating === "good"
                  ? "border-green-400 ring-1 ring-green-300"
                  : st?.rating === "bad"
                  ? "border-red-400 ring-1 ring-red-300"
                  : "border-gray-200";
              return (
                <div
                  key={key}
                  className={`overflow-hidden rounded-lg border transition ${ring}`}
                >
                  <ProductCard
                    name={cand.name}
                    brand={cand.brand}
                    serving={cand.serving}
                    nutrients={cand.nutrients}
                    netCarbs={cand.net_carbs_g}
                    ketoScore={cand.keto_score}
                    upfScore={cand.upf_score}
                    country={cand.country}
                    notes={cand.notes}
                    ingredients={cand.ingredients}
                    aiScore={cand.ai_score}
                    aiReason={cand.ai_reason}
                    refCode={cand.ref_code}
                    images={cand.images}
                    onViewFull={() => onViewFull(cand.ref_code, cand.name)}
                  />
                  {/* Rating controls — clearly labeled, always the card footer. */}
                  <div className="border-t border-gray-100 bg-gray-50/60 px-3.5 py-2.5">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                        Rate this pick
                      </span>
                      <button
                        type="button"
                        onClick={() => onRate(key, "good")}
                        className={`inline-flex items-center gap-1 rounded-md px-3 py-1.5 text-sm font-semibold transition ${
                          st?.rating === "good"
                            ? "bg-green-600 text-white shadow"
                            : "bg-white text-green-700 ring-1 ring-green-200 hover:bg-green-50"
                        }`}
                      >
                        👍 Good
                      </button>
                      <button
                        type="button"
                        onClick={() => onRate(key, "bad")}
                        className={`inline-flex items-center gap-1 rounded-md px-3 py-1.5 text-sm font-semibold transition ${
                          st?.rating === "bad"
                            ? "bg-red-600 text-white shadow"
                            : "bg-white text-red-700 ring-1 ring-red-200 hover:bg-red-50"
                        }`}
                      >
                        👎 Bad
                      </button>
                      {st?.rating && (
                        <span className="text-xs font-medium text-gray-400">
                          ✓ included in your review
                        </span>
                      )}
                    </div>
                    <input
                      type="text"
                      value={st?.note || ""}
                      onChange={(e) => onNote(key, e.target.value)}
                      placeholder="Optional note — why is this a good or bad replacement?"
                      className="mt-2 w-full rounded-md border border-gray-300 bg-white px-2.5 py-1.5 text-sm focus:border-indigo-500 focus:outline-none"
                    />
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default GroceryReplacementTab;
